import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  ChangeDetectorRef,
  HostBinding,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ChatbotService, ChatMessage, ChatResponse } from '../../../services/chatbot.service';


@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss'],
})
export class ChatbotComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('messagesEnd') messagesEnd!: ElementRef;
  @ViewChild('inputField') inputField!: ElementRef;

  isOpen   = false;
  isLoading = false;
  userInput = '';
  theme: 'light' | 'dark' = 'light';
  messages: ChatMessage[] = [];
  suggestions: string[] = [];
  private shouldScroll = false;

  private subs: Subscription[] = [];

  @HostBinding('class.dark-theme') get isDarkTheme() {
    return this.theme === 'dark';
  }

  // ── Quick-start suggestions for empty state ─────────────────────────────
  readonly quickStarts = [
    "📅 Today's appointments",
    '🏥 Show OPD queue',
    '👥 Show recent leads',
    '📋 Create an appointment',
  ];

  constructor(
    private chatbot: ChatbotService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.theme = (localStorage.getItem('tina-chatbot-theme') as 'light' | 'dark') || 'light';
    this.subs.push(
      this.chatbot.isOpen$.subscribe((open: boolean) => {

        this.isOpen = open;
        if (open) {
          this.messages = this.chatbot.getHistory();
          setTimeout(() => this.inputField?.nativeElement?.focus(), 100);
        }
        this.cdr.detectChanges();
      }),
      this.chatbot.isLoading$.subscribe((loading: boolean) => {

        this.isLoading = loading;
        if (loading) this.shouldScroll = true;
        this.cdr.detectChanges();
      }),
    );
  }

  ngAfterViewChecked() {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

  // ── UI actions ───────────────────────────────────────────────────────────

  toggleTheme() {
    this.theme = this.theme === 'light' ? 'dark' : 'light';
    localStorage.setItem('tina-chatbot-theme', this.theme);
  }

  toggleChat() { this.chatbot.toggle(); }
  closeChat()  { this.chatbot.close(); }
  clearChat()  { this.chatbot.clearHistory(); this.messages = []; this.suggestions = []; }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  useQuickStart(text: string) { this.userInput = text; this.send(); }
  useSuggestion(chip: string) { this.userInput = chip; this.send(); }

  send() {
    const msg = this.userInput.trim();
    if (!msg || this.isLoading) return;

    this.userInput = '';
    this.suggestions = [];
    
    // Send message triggers adding the user message to history and sets loading to true
    const obs$ = this.chatbot.sendMessage(msg);
    
    // Immediately reflect the updated history with user's message in the UI
    this.messages = [...this.chatbot.getHistory()];
    this.shouldScroll = true;
    this.cdr.detectChanges();

    obs$.subscribe({
      next: (res: ChatResponse) => {
        this.chatbot.addAssistantResponse(res.reply);
        this.messages = [...this.chatbot.getHistory()];
        this.suggestions = res.suggestions || [];
        this.shouldScroll = true;
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        console.error('[Chatbot] Error:', err);
        this.chatbot.addAssistantResponse(
          "I'm having trouble connecting right now. Please try again in a moment. 🔄"
        );
        this.chatbot.setLoading(false);
        this.messages = [...this.chatbot.getHistory()];
        this.shouldScroll = true;
        this.cdr.detectChanges();
      },
    });
  }

  private scrollToBottom() {
    try {
      this.messagesEnd?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    } catch {}
  }

  // ── Utility ──────────────────────────────────────────────────────────────

  /** Format assistant message — convert **bold** and newlines */
  formatMessage(content: string): string {
    return content
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br>');
  }

  trackByIndex(index: number) { return index; }
}
